/**
 * PhysicsModule — client-side Rapier collision for the avatar (Graphics G1).
 *
 * Wires the engine's standalone `physics3d` (Rapier) + a kinematic character
 * controller into the Atlas client (which otherwise has no physics). Static
 * colliders are built from the cooked collider proxies (trimesh walls for enterable
 * structures, convex hulls for solid props) and the avatar is a kinematic capsule.
 *
 * The avatar's authoritative XZ stays server-driven: `moveAvatar` resolves the move
 * locally (slide on walls / stand on floors / climb steps) and returns the actual
 * horizontal displacement, so the caller feeds the COLLISION-SAFE direction back to
 * the unchanged `move` reducer (the server keeps its speed/teleport authority).
 * Vertical grounding is client-side: the KCC for structure floors, a `terrainHeight`
 * clamp for the infinite procedural terrain.
 *
 * Rapier WASM inits asynchronously; everything gates on `ready` (the avatar falls
 * back to naive integration until then).
 */
import type { App, IModule } from 'enginex';
// Type-only imports (erased at build). The VALUE imports happen dynamically in init():
// @dimforge/rapier3d-compat base64-inlines its ~1.6 MB WASM (~2.2 MB of JS), and this
// module is the ONLY static value-import of physics in the client graph — deferring it
// moves the whole blob out of the critical main chunk into a lazy parallel chunk
// (the deep-research bundle finding). Everything already gates on `ready`, so the
// game tolerates physics arriving late by design.
import type { PhysicsWorld3D } from 'enginex/physics3d/PhysicsWorld3D.js';
import type { CharController } from 'enginex/physics3d/CharController.js';
// Type-only, via the SIMD package name: resolves up-tree to the ENGINE's copy,
// giving these types the same declaration identity as the engine's re-exported
// RAPIER value (the aliased local '-compat' install is a distinct copy whose
// private members make it nominally incompatible). Erased at runtime.
import type RAPIER_NS from '@dimforge/rapier3d-simd-compat';
import { colliderDescFromProxy, type ColliderProxy } from '../lib/collider_proxy.js';
import { groundYIn } from '../lib/grounding.js';
import { atlas } from '../state.js';
import { activePreset } from '../lib/quality_tiers.js';
import { waterStillAt, waterLiveAt, splashWater, stampWater } from '../lib/water_fx.js';
import { waterModeFor, swimVerticalStep, wadeSpeedFactor, splashFor, SPLASH_MIN_IMPACT_MPS, type WaterMode } from '../lib/water_physics.js';
import { sfx } from '../lib/sfx.js';
import { classifyMantle, mantleProfile, MANTLE_MAX_M, MANTLE_TICKS, MANTLE_PROBE_M, MANTLE_LAND_M, MANTLE_WALL_NY_MAX } from '../lib/mantle.js';
import { Logger } from 'enginex/core/Logger.js';

/** Capsule: halfHeight 0.6 + radius 0.3 → ~1.8 m; center sits this far above the feet. */
const CAPSULE_HALF_HEIGHT = 0.6;
const CAPSULE_RADIUS = 0.3;
const CAPSULE_CENTER_OFFSET = CAPSULE_HALF_HEIGHT + CAPSULE_RADIUS; // 0.9
/** Crouch (build-now wave 2, 2026-09-12): the same capsule with a shorter
 *  segment — 0.25 + 0.3 → 1.1 m tall, feet where they were (the resize is
 *  about the FEET, never the centre: a centre-preserving shrink would float
 *  the feet 0.35 m and the terrain clamp would then yank them down). */
const CROUCH_HALF_HEIGHT = 0.25;
const CROUCH_CENTER_OFFSET = CROUCH_HALF_HEIGHT + CAPSULE_RADIUS; // 0.55
/** Jump launch speed: v²/2g ≈ 1.9 m apex, ~1.2 s of air — clears what autostep can't. */
const JUMP_SPEED_MPS = 6.1;
/** Coyote time: a jump pressed this soon after walking off an edge still fires. */
const COYOTE_S = 0.12;

export interface ColliderTransform { x: number; y: number; z: number; yaw: number; scale: number; }
export interface AvatarMove { x: number; z: number; feetY: number; movedX: number; movedZ: number; grounded: boolean }
/** A scanned mantle: the obstacle's top, the landing on it, the face's distance. */
export interface MantleTarget { topY: number; landX: number; landZ: number; wallDist: number }

export class PhysicsModule implements IModule {
  readonly name = 'PhysicsModule';
  private phys: PhysicsWorld3D | null = null;
  private kcc: CharController | null = null;
  private rapier: typeof RAPIER_NS | null = null; // the lazily-imported Rapier namespace
  private body: RAPIER_NS.RigidBody | null = null;
  private cap: RAPIER_NS.Collider | null = null;
  private readonly statics = new Map<string, RAPIER_NS.RigidBody>();
  // Construction classes + collider SOURCES retained for the poisoned-world
  // rebuild: a stuck wasm borrow flag ("recursive use of an object") makes
  // every Rapier call throw forever — the only cure is a fresh world, and a
  // fresh world needs everything re-added (2026-07-30, the planetfall
  // paralysis console).
  private PhysCls: (new (g: { x: number; y: number; z: number }) => PhysicsWorld3D) | null = null;
  private KccCls: (new (w: PhysicsWorld3D, o: Record<string, number>) => CharController) | null = null;
  private readonly staticSrc = new Map<string, { proxy: ColliderProxy; t: ColliderTransform }>();
  /** Statics requested BEFORE Rapier finished its lazy import: materialized
   *  the moment `ready` flips (a silent drop here left the login city's
   *  colliders marked active-but-absent in the settlement ring — the
   *  "no collision when walking" class, 2026-09-03). */
  private readonly pendingStatics = new Set<string>();
  private readonly terrainSrc = new Map<string, { v: Float32Array; i: Uint32Array; x: number; z: number }>();
  private lastRebuildMs = 0;
  private vy = 0;
  private _grounded = false;
  private _spawnGrounded = false; // one-time: seat the capsule on terrain on its first tick (the body spawns above ground)
  private _disposed = false; // guard against the async Rapier init completing after dispose()
  private airT = 0;           // seconds since last grounded (coyote-time clock)
  private snapOff = false;    // snap-to-ground disabled while ascending (else Rapier glues the jump down)
  ready = false;
  /** The LIVE capsule's centre height above the feet: 0.9 standing, 0.55 crouched. */
  private centerOffset = CAPSULE_CENTER_OFFSET;
  private crouched_ = false;
  get crouched(): boolean { return this.crouched_; }

  /**
   * Crouch / stand (build-now wave 2). Crouching always succeeds; standing
   * succeeds only where the STANDING capsule fits (an overhead shape test
   * against every collider but our own — under a low ceiling the player
   * stays crouched and the caller keeps asking each step). The capsule is
   * resized about the feet and the world is stepped so the next query sees
   * the new shape. Returns whether the requested stance holds now.
   */
  setCrouch(on: boolean): boolean {
    if (!this.ready || !this.cap || !this.body || !this.phys) return false;
    if (on === this.crouched_) return true;
    if (!on && !this.standingClear()) return false;
    const cur = this.body.translation();
    const feetY = cur.y - this.centerOffset;
    this.cap.setHalfHeight(on ? CROUCH_HALF_HEIGHT : CAPSULE_HALF_HEIGHT);
    this.centerOffset = on ? CROUCH_CENTER_OFFSET : CAPSULE_CENTER_OFFSET;
    this.body.setTranslation({ x: cur.x, y: feetY + this.centerOffset, z: cur.z }, true);
    this.crouched_ = on;
    this.phys.world.step();
    return true;
  }

  // ─── Mantle (build-now wave 6) ───────────────────────────────────────────
  private mantle: { t: number; sx: number; sy: number; sz: number; target: MantleTarget } | null = null;
  get mantling(): boolean { return this.mantle !== null; }

  /**
   * Look for a mantle ahead along (dirX, dirZ): a WALL within MANTLE_PROBE_M
   * (the live capsule cast forward hits a face steeper than
   * MANTLE_WALL_NY_MAX), a TOP in the mantle band (a ray down onto the
   * obstacle just past its face), STANDING ROOM on that top and HEADROOM
   * for the rise where we stand (the standing capsule fits at both). The
   * guide's five forward traces + capsule clearance, reduced to one shape
   * cast, one ray and two overlap tests. Null when any test fails.
   */
  mantleScan(dirX: number, dirZ: number): MantleTarget | null {
    if (!this.ready || !this.body || !this.cap || !this.phys || !this.rapier || this.mantle) { this.mantleWhy = 'not ready'; return null; }
    const len = Math.hypot(dirX, dirZ);
    if (len < 1e-6) { this.mantleWhy = 'no direction'; return null; }
    const dx = dirX / len, dz = dirZ / len;
    const cur = this.body.translation();
    const feetY = cur.y - this.centerOffset;
    const rot = { x: 0, y: 0, z: 0, w: 1 };
    const exclude = this.cap;
    // 1. The wall: a capsule a little SHORTER than the live one, swept
    // forward from a little ABOVE the live centre — the standing capsule
    // rests on the floor within the KCC's skin, so a cast of the live shape
    // reports the floor (toi 0, normal up) as its first hit and never the
    // wall (the traversal harness's first run, 2026-09-12).
    const half = (this.crouched_ ? CROUCH_HALF_HEIGHT : CAPSULE_HALF_HEIGHT) - 0.12;
    const probe = new this.rapier.Capsule(half, CAPSULE_RADIUS);
    const origin = { x: cur.x, y: cur.y + 0.1, z: cur.z };
    const wall = this.phys.world.castShape(origin, rot, { x: dx, y: 0, z: dz }, probe, 0, MANTLE_PROBE_M, true, undefined, undefined, exclude);
    if (!wall) { this.mantleWhy = 'no wall ahead'; return null; }
    const wh = wall as unknown as { time_of_impact?: number; timeOfImpact?: number; toi?: number };
    const wallDist = wh.time_of_impact ?? wh.timeOfImpact ?? wh.toi ?? 0;
    if (Math.abs(wall.normal2.y) > MANTLE_WALL_NY_MAX) { this.mantleWhy = `not a face (ny ${wall.normal2.y.toFixed(2)})`; return null; }
    // 2. The top: a ray down just past the face, from above the band.
    const landX = cur.x + dx * (wallDist + CAPSULE_RADIUS + MANTLE_LAND_M);
    const landZ = cur.z + dz * (wallDist + CAPSULE_RADIUS + MANTLE_LAND_M);
    const from = feetY + MANTLE_MAX_M + 0.5;
    const ray = new this.rapier.Ray({ x: landX, y: from, z: landZ }, { x: 0, y: -1, z: 0 });
    const top = this.phys.world.castRay(ray, MANTLE_MAX_M + 0.5, true, undefined, undefined, exclude);
    if (!top) { this.mantleWhy = 'no top past the face'; return null; }
    // Ray hits carry `timeOfImpact` (camelCase); shape-cast hits `time_of_impact` — both read here.
    const th = top as unknown as { timeOfImpact?: number; time_of_impact?: number; toi?: number };
    const topY = from - (th.timeOfImpact ?? th.time_of_impact ?? th.toi ?? 0);
    const cls = classifyMantle(topY - feetY);
    if (!cls.ok) { this.mantleWhy = `${cls.why} (${(topY - feetY).toFixed(2)} m)`; return null; }
    // 3. Standing room on the top, 4. headroom for the rise where we stand.
    const standing = new this.rapier.Capsule(CAPSULE_HALF_HEIGHT, CAPSULE_RADIUS);
    const cy = topY + CAPSULE_CENTER_OFFSET + 0.04;
    if (this.phys.world.intersectionWithShape({ x: landX, y: cy, z: landZ }, rot, standing, undefined, undefined, exclude)) { this.mantleWhy = 'no standing room on top'; return null; }
    if (this.phys.world.intersectionWithShape({ x: cur.x, y: cy, z: cur.z }, rot, standing, undefined, undefined, exclude)) { this.mantleWhy = 'no headroom for the rise'; return null; }
    this.mantleWhy = 'ok';
    return { topY, landX, landZ, wallDist };
  }

  /** Why the last mantleScan said no (diag + harnesses). */
  mantleWhy = '';

  /** Commit to a scanned mantle: the next MANTLE_TICKS steps follow the
   *  profile (rise, then forward) through the KCC; a crouched player stands
   *  first (the scan proved the room). */
  beginMantle(target: MantleTarget | null): boolean {
    if (!target || !this.ready || !this.body || !this.kcc || this.mantle) return false;
    if (this.crouched_ && !this.setCrouch(false)) return false;
    const cur = this.body.translation();
    this.mantle = { t: 0, sx: cur.x, sy: cur.y, sz: cur.z, target };
    this.vy = 0;
    this._grounded = false;
    this.kcc.disableSnapToGround();
    this.snapOff = true;
    sfx('sfx_jump', { gain: 0.25, rate: 0.9, cooldownMs: 200 });
    return true;
  }

  /** One fixed step of a committed mantle (called from moveAvatarInner). */
  private stepMantle(t: { x: number; y: number; z: number }, dt: number): AvatarMove {
    const mt = this.mantle!;
    const kcc = this.kcc!, cap = this.cap!, body = this.body!, phys = this.phys!;
    mt.t++;
    const k = Math.min(1, mt.t / MANTLE_TICKS);
    const prof = mantleProfile(k);
    const goalY = mt.target.topY + this.centerOffset + 0.02;
    const wantX = mt.sx + (mt.target.landX - mt.sx) * prof.forward;
    const wantY = mt.sy + (goalY - mt.sy) * prof.rise;
    const wantZ = mt.sz + (mt.target.landZ - mt.sz) * prof.forward;
    // Through the KCC: a wall that closed in mid-climb still stops the body.
    const c = kcc.move(cap, { x: wantX - t.x, y: wantY - t.y, z: wantZ - t.z }, dt);
    const nx = t.x + c.x, ny = t.y + c.y, nz = t.z + c.z;
    body.setNextKinematicTranslation({ x: nx, y: ny, z: nz });
    phys.world.step();
    this.vy = 0;
    this.airT = 0;
    this._grounded = false;
    if (k >= 1) {
      this.mantle = null;
      this._grounded = true;
      if (this.snapOff) { kcc.enableSnapToGround(0.5); this.snapOff = false; }
    }
    return { x: nx, z: nz, feetY: ny - this.centerOffset, movedX: c.x, movedZ: c.z, grounded: this._grounded };
  }

  /** Would the full standing capsule fit where the feet are now? Excludes the
   *  avatar's own collider; a 3 cm lift keeps the floor from grazing the tip. */
  standingClear(): boolean {
    if (!this.ready || !this.body || !this.phys || !this.rapier) return true;
    const cur = this.body.translation();
    const feetY = cur.y - this.centerOffset;
    const shape = new this.rapier.Capsule(CAPSULE_HALF_HEIGHT, CAPSULE_RADIUS);
    const hit = this.phys.world.intersectionWithShape(
      { x: cur.x, y: feetY + CAPSULE_CENTER_OFFSET + 0.03, z: cur.z }, { x: 0, y: 0, z: 0, w: 1 }, shape,
      undefined, undefined, this.cap ?? undefined,
    );
    return hit === null;
  }

  /**
   * Launch a jump if grounded — or within the coyote window just after leaving
   * the ground (stepping off an edge no longer eats the press). Returns whether
   * it fired so the caller can keep buffering an early press.
   */
  /** Gravity multiplier (Edit Mode gravity_zone devices); 1 = normal. */
  gravityScale = 1;

  /** Launch the avatar upward at `vy` m/s regardless of grounding (jump pads). */
  launch(vy: number): void {
    if (!this.ready || !this.kcc) return;
    sfx('sfx_jump', { gain: 0.45, rate: 0.8, cooldownMs: 300 }); // jump pad: the same whoosh, deeper
    this.vy = Math.max(this.vy, vy);
    this._grounded = false;
    this.airT = COYOTE_S + 1;
    this.kcc.disableSnapToGround();
    this.snapOff = true;
  }

  /** Interactive water (2026-09-11): dry / wade / swim — the avatar module picks the swim speed from it. */
  private waterMode_: WaterMode = 'dry';
  get waterMode(): WaterMode { return this.waterMode_; }
  /** Feet on something (the KCC's verdict, this fixed step) — footsteps are CONTACTS, not held keys (audio, 2026-09-20). */
  get grounded(): boolean { return this._grounded; }
  /** The jump key while swimming = a stroke (consumed by the next fixed step). */
  private strokeRequested = false;

  jump(): boolean {
    if (!this.ready || !this.kcc) return false;
    if (this.waterMode_ === 'swim') {
      this.strokeRequested = true;
      sfx('sfx_water_splash', { gain: 0.22, cooldownMs: 450 });
      return true;
    }
    if (!this._grounded && this.airT > COYOTE_S) return false;
    if (this.vy > 1) return false; // already ascending — no double-launch
    this.vy = JUMP_SPEED_MPS;
    sfx('sfx_jump', { gain: 0.3, cooldownMs: 250 }); // the launch whoosh
    this._grounded = false;
    this.airT = COYOTE_S + 1; // consume the coyote window (one jump per ground contact)
    // Rapier's snap-to-ground pulls the capsule back down during the ascent —
    // the "jump never launches" bug. Off while rising; restored when falling.
    this.kcc.disableSnapToGround();
    this.snapOff = true;
    return true;
  }

  init(_app: App): void {
    void (async () => {
      // Dynamic import = the code-split point: physics3d + the base64 Rapier WASM load
      // as their own chunk, in parallel with everything else, off the critical path.
      const [{ initRapier3D, PhysicsWorld3D, RAPIER }, { CharController }] = await Promise.all([
        import('enginex/physics3d/PhysicsWorld3D.js'),
        import('enginex/physics3d/CharController.js'),
      ]);
      await initRapier3D();
      // Bail early if dispose() ran before Rapier finished loading — without this
      // guard the completion sets this.phys/ready and resurrects disposed state.
      if (this._disposed) return;
      this.rapier = RAPIER;
      this.PhysCls = PhysicsWorld3D as never;
      this.KccCls = CharController as never;
      this.phys = new PhysicsWorld3D({ x: 0, y: -9.81, z: 0 });
      this.body = this.phys.createBody(RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(0, CAPSULE_CENTER_OFFSET, 0));
      this.cap = this.phys.createCollider(RAPIER.ColliderDesc.capsule(CAPSULE_HALF_HEIGHT, CAPSULE_RADIUS), this.body);
      // Rapier's narrow phase computes NO contact manifolds between a KINEMATIC
      // body and FIXED bodies by default (ActiveCollisionTypes excludes
      // KINEMATIC_FIXED) — the KCC never needed them (it shape-casts), but the
      // depenetration pass READS manifolds; without this flag it silently saw
      // zero contacts in the live game (probe: depen=0 while visibly embedded).
      this.cap.setActiveCollisionTypes(RAPIER.ActiveCollisionTypes.DEFAULT | RAPIER.ActiveCollisionTypes.KINEMATIC_FIXED);
      // 62° climb / 68° slide: the v2 relief's mountain belts routinely exceed the
      // old 45° limit, which made whole highlands unwalkable. 62° keeps genuine
      // cliffs (the ridged crests) unclimbable while ordinary steep slopes are
      // traversable; a taller autostep swallows terrain-mesh lips and stray rocks.
      this.kcc = new CharController(this.phys, {
        snapToGround: 0.5,
        autoStepMaxHeight: 0.8, // 0.55 caught seg-48 facet lips on steep slopes (terrain stutter)
        autoStepMinWidth: 0.15,
        maxSlopeClimbAngle: (62 * Math.PI) / 180,
        minSlopeSlideAngle: (68 * Math.PI) / 180,
      });
      this.phys.world.step();
      this.ready = true;
      // Materialize every static that arrived while Rapier was still loading.
      if (this.pendingStatics.size > 0) {
        const ids = [...this.pendingStatics];
        this.pendingStatics.clear();
        let added = 0;
        for (const id of ids) {
          const src = this.staticSrc.get(id);
          if (!src) continue;
          this.staticSrc.delete(id); // addStaticCollider re-records it
          if (this.addStaticCollider(id, src.proxy, src.t)) added++;
        }
        Logger.info('Physics', `Rapier ready — materialized ${added}/${ids.length} static colliders queued during init`);
      }
      (globalThis as unknown as { __physDiag?: () => { maxRawMps: number; capped: number; grounded: boolean; vy: number; airT: number; snapOff: boolean; water: WaterMode; feetY: number; statics: number; clampFight: number } }).__physDiag =
        () => ({
          maxRawMps: Math.round(this.diag.maxRawMps * 10) / 10, capped: this.diag.capped,
          // Live capsule state (no world.step — safe to poll from a probe every frame).
          grounded: this._grounded, vy: this.vy, airT: this.airT, snapOff: this.snapOff, water: this.waterMode_,
          feetY: this.body ? this.body.translation().y - this.centerOffset : NaN,
          statics: this.statics.size, clampFight: this.clampFightTicks,
        });
    })().catch(() => { /* physics unavailable — the avatar uses naive integration */ });
  }

  /**
   * Add a static collider (a placed structure/prop) at a world transform.
   *
   * NO world.step() here (the movement-freeze fix): a settlement streaming in
   * adds ~70 colliders in one frame, and stepping the ENTIRE world per add
   * (all terrain trimeshes + every static) froze the frame for up to a second.
   * moveAvatar() already steps the world EVERY fixed tick, so a new collider
   * becomes solid within <=16 ms — imperceptible, and 70x cheaper.
   */
  addStaticCollider(id: string, proxy: ColliderProxy, t: ColliderTransform): boolean {
    if (this.statics.has(id)) return true;
    if (!this.phys || !this.rapier) {
      // Not ready yet (the lazy Rapier chunk): keep the source and add it on
      // ready — never a silent drop (callers treat the add as done).
      if (this._disposed) return false;
      this.staticSrc.set(id, { proxy, t });
      this.pendingStatics.add(id);
      return true;
    }
    const desc = colliderDescFromProxy(this.rapier, scaledProxy(proxy, t.scale));
    if (!desc) return false;
    const body = this.phys.createBody(
      this.rapier.RigidBodyDesc.fixed()
        .setTranslation(t.x, t.y, t.z)
        .setRotation({ x: 0, y: Math.sin(t.yaw / 2), z: 0, w: Math.cos(t.yaw / 2) }),
    );
    this.phys.createCollider(desc, body);
    this.statics.set(id, body);
    this.staticSrc.set(id, { proxy, t });
    return true;
  }

  /** Number of live static collider bodies (probe / settle report). */
  get staticColliderCount(): number { return this.statics.size; }

  removeStaticCollider(id: string): void {
    const body = this.statics.get(id);
    if (body && this.phys) { this.phys.removeBody(body); this.statics.delete(id); }
    this.staticSrc.delete(id);
    this.pendingStatics.delete(id);
  }

  // ── Terrain trimesh colliders (exact-match slope/cliff collision) ────────────────

  private readonly terrainCols = new Map<string, RAPIER_NS.RigidBody>();

  /** Probe surface (`__physDiag`): how fast the capsule TRIED to go, and how often the step cap engaged. */
  readonly diag = { maxRawMps: 0, capped: 0 };

  /** True when a terrain collider exists for this chunk key. */
  hasTerrainCollider(key: string): boolean { return this.terrainCols.has(key); }

  /**
   * Add a static TRIMESH collider built from a terrain chunk's render geometry —
   * physics matches the visual mesh by construction (no heightfield row-order
   * ambiguity). `vertices` are the chunk's LOCAL positions (origin-centered, Y
   * displaced); the body sits at the chunk's world origin. With real terrain
   * geometry under the KCC, its maxSlopeClimbAngle (45°) makes steep slopes and
   * cliffs genuinely unclimbable instead of the lift-anywhere analytic clamp.
   */
  addTerrainCollider(key: string, vertices: Float32Array, indices: Uint32Array, originX: number, originZ: number): void {
    if (!this.phys || !this.rapier || this.terrainCols.has(key)) return;
    const body = this.phys.createBody(this.rapier.RigidBodyDesc.fixed().setTranslation(originX, 0, originZ));
    this.phys.createCollider(this.rapier.ColliderDesc.trimesh(vertices, indices), body);
    this.terrainCols.set(key, body);
    this.terrainSrc.set(key, { v: vertices, i: indices, x: originX, z: originZ });
    // No world.step() — the per-tick moveAvatar step integrates it (freeze fix).
  }

  removeTerrainCollider(key: string): void {
    const body = this.terrainCols.get(key);
    if (body && this.phys) { this.phys.removeBody(body); this.terrainCols.delete(key); }
    this.terrainSrc.delete(key);
  }

  /**
   * Teleport the avatar to an EXPLICIT world position, Y included (the
   * sky-ship boarding lift). Callers set `atlas.interiorId` first so the
   * terrain clamp / reconcile snap / unstick watchdogs stand down — the
   * altitude-interior regime. Vertical velocity zeroes so the arrival is a
   * stand, never a fall.
   */
  teleportAvatarY(x: number, y: number, z: number): void {
    if (!this.body) return;
    try {
      this.vy = 0;
      this.body.setTranslation({ x, y: y + this.centerOffset, z }, true);
      this.phys?.world.step();
      atlas.localX = x;
      atlas.localY = y;
      atlas.localZ = z;
    } catch (e) {
      if (PhysicsModule.isPoisonError(e)) { this.rebuildPoisonedWorld('teleportAvatarY'); return; }
      throw e;
    }
  }

  /** Snap the avatar capsule to a world XZ (used on reconcile, grounded to terrain). */
  teleportAvatar(x: number, z: number, keepY = false): void {
    try {
      this.teleportAvatarInner(x, z, keepY);
    } catch (e) {
      if (PhysicsModule.isPoisonError(e)) { this.rebuildPoisonedWorld('teleportAvatar'); return; }
      throw e;
    }
  }

  private teleportAvatarInner(x: number, z: number, keepY = false): void {
    if (!this.body) return;
    // keepY: an XZ-only nudge (the reconcile's smooth correction) — the
    // capsule keeps its height and its air state. Seating Y at the terrain
    // here was the "jump on an upper floor teleports me to the first floor"
    // field bug: every correction re-seated the player at GROUND level, and
    // jumping (send-path divergence spike) reliably triggered one. Interiors
    // always keep Y — the ground truth indoors is the slab, not the terrain.
    if (keepY || atlas.interiorId !== null || atlas.caveId !== null) {
      const cur = this.body.translation();
      this.body.setTranslation({ x, y: cur.y, z }, true);
      this.phys?.world.step();
      return;
    }
    const conn = atlas.conn;
    let feetY = conn ? groundYIn(conn, atlas.currentWorldId, x, z) : 0;
    // RAY-AWARE SEAT: the analytic ground lies about pads/floors and about
    // chunks whose stamps loaded after the seat — take the HIGHEST solid
    // surface in the pad window instead (field report 2026-08-23: pod
    // ejection buried the player under a stamped pad).
    this.phys?.world.step(); // streamed colliders become query-visible
    const top = this.topSurfaceAt(x, z, feetY);
    if (top !== null && top > feetY) feetY = top;
    // DROP-IN: start slightly above the ground and fall onto the highest
    // solid thing at (x,z) — never embedded inside a pad structure.
    this.body.setTranslation({ x, y: feetY + PhysicsModule.DROP_IN_M + this.centerOffset, z }, true);
    this.vy = 0;
    this._grounded = false;       // falling — the KCC lands it on terrain OR a floor collider
    this._spawnGrounded = true;   // seated (airborne) — no first-tick re-snap needed
    this.airT = 0;
    if (this.snapOff && this.kcc) { this.kcc.enableSnapToGround(0.5); this.snapOff = false; } // a mid-jump teleport lands
    this.phys?.world.step();
  }

  /**
   * Cave interiors (terrain wave 2026-09-08): the cave module registers a test
   * "which cavity holds this capsule centre?" and the terrain clamp asks it at
   * the BODY's own position every tick — `atlas.localX/Y/Z` lag a tick behind
   * the body, and a stale "outside" answer let the clamp yank a capsule that
   * had just entered a tunnel back up onto the hill.
   */
  private caveTest: ((x: number, y: number, z: number) => string | null) | null = null;
  setCaveTest(fn: ((x: number, y: number, z: number) => string | null) | null): void { this.caveTest = fn; }

  /**
   * Debug/probe placement: put the capsule's FEET at (x, y, z) exactly — no
   * ground seat, no drop-in. Used by the cave probe lever (`__caveEnter`) to
   * stand the avatar inside a cavity whose analytic ground lies above it; the
   * next server reconcile keeps this Y because `atlas.caveId` is set there.
   */
  placeFeet(x: number, y: number, z: number): void {
    if (!this.body) return;
    this.body.setTranslation({ x, y: y + this.centerOffset, z }, true);
    this.vy = 0;
    this._grounded = false;
    this.phys?.world.step();
  }

  // ─── Spawn-stuck safety ──────────────────────────────────────────────────────
  // Outward ring scan for a clear spot; max radius stays under the server's
  // 24 m unstickNudge envelope so the realigning reducer never rejects.
  private static readonly UNSTICK_RINGS = [2, 3.5, 5, 7, 10, 14, 18, 22, 30, 45, 65, 90, 120, 140];
  /** Teleports/spawns seat the capsule this far ABOVE the analytic ground and
   *  let it FALL — it lands on whatever is actually solid there (terrain, or
   *  a structure pad/floor ABOVE the analytic ground) instead of starting
   *  embedded at the analytic height inside a building (field request
   *  2026-08-23: "drop me onto the terrain from a slight height"). */
  private static readonly DROP_IN_M = 1.25;

  /** True when the last moveAvatar()'s contact normals opposed (pinched, not wall-pressed). */
  private lastWedged = false;
  get wedged(): boolean { return this.lastWedged; }
  /** Consecutive ticks the terrain clamp has fought the depenetration pass
   *  (capsule at/below analytic ground WHILE overlapping a collider). The
   *  avatar watchdog treats a sustained fight as trapped and relocates. */
  private clampFightTicks = 0;
  get clampFight(): number { return this.clampFightTicks; }

  constructor() {
    // Probe diagnostics: physics readiness + collider census.
    (globalThis as unknown as { __phys?: () => { ready: boolean; statics: number; pending: number; terrain: number; at: number[][] } }).__phys =
      () => ({
        ready: this.ready, statics: this.statics.size, pending: this.pendingStatics.size, terrain: this.terrainCols.size,
        at: [...this.statics.values()].slice(0, 60).map((b) => { const t = b.translation(); return [Math.round(t.x), Math.round(t.z)]; }),
      });
    // Probe rig: inject a synthetic static box through the PRODUCTION
    // addStaticCollider path (streamed-city assembly is too flaky headless to
    // be the fixture for the unstick proof). yOff 0 = embed box; yOff ~8 = roof slab.
    (globalThis as unknown as { __physAddBox?: (x: number, z: number, yOff?: number, half?: number) => boolean }).__physAddBox =
      (x, z, yOff = 0, half = 10) => {
        if (!this.ready || !atlas.conn) return false;
        const feetY = groundYIn(atlas.conn, atlas.currentWorldId, x, z);
        this.addStaticCollider(`__probe:${x}:${z}:${yOff}`, { shape: 'box', halfExtents: [half, half, half] }, { x, y: feetY + yOff, z, yaw: 0, scale: 1 });
        return this.statics.has(`__probe:${x}:${z}:${yOff}`);
      };
    // Probe rig: a synthetic TERRAIN-class trimesh patch (a horizontal quad at
    // capsule-middle height) through the PRODUCTION addTerrainCollider path —
    // the deterministic fixture for the terrain-embed watchdog case.
    (globalThis as unknown as { __physAddTerrainPatch?: (x: number, z: number, lift?: number) => boolean }).__physAddTerrainPatch =
      (x, z, lift = 0.9) => {
        if (!this.ready || !atlas.conn) return false;
        const feetY = groundYIn(atlas.conn, atlas.currentWorldId, x, z);
        const y = feetY + lift;
        const H = 4;
        const verts = new Float32Array([-H, y, -H, H, y, -H, H, y, H, -H, y, H]);
        const idx = new Uint32Array([0, 1, 2, 0, 2, 3]);
        const key = `__probe-terrain:${x}:${z}`;
        this.addTerrainCollider(key, verts, idx, x, z);
        return this.terrainCols.has(key);
      };
    // Probe rig: DIRECT capsule seat via teleportAvatar — bypasses the whole
    // reconcile pipeline (grace windows + hard-snap timing made teleport-seeded
    // seating nondeterministic under SwiftShader; two probe runs died on it).
    (globalThis as unknown as { __physSeat?: (x: number, z: number) => boolean }).__physSeat =
      (x, z) => {
        if (!this.ready) return false;
        this.teleportAvatar(x, z);
        atlas.localX = x;
        atlas.localZ = z;
        return true;
      };
    // Probe diagnostics: the capsule's own trap census at its REAL body position
    // (an XZ+ground-level probe reads the wrong volume once the capsule stands
    // ON something — the exact artifact that failed the first green probes).
    (globalThis as unknown as { __physSelf?: () => { x: number; z: number; feetY: number; overlap: boolean; ceiling: boolean; terrainOverlap: boolean } | null }).__physSelf =
      () => {
        if (!this.ready || !this.body) return null;
        this.phys?.world.step();
        const cur = this.body.translation();
        const feetY = cur.y - this.centerOffset;
        const structs = this.structureHandles();
        return {
          x: cur.x, z: cur.z, feetY,
          overlap: this.capsuleOverlapsStructure(cur.x, feetY, cur.z, structs),
          ceiling: this.hasCeilingAbove(cur.x, feetY, cur.z, structs),
          terrainOverlap: this.capsuleEmbeddedInTerrain(cur.x, feetY, cur.z),
        };
      };
    // Probe rig: the raw Rapier namespace + live objects, so a harness can wrap
    // entry points and catch the FIRST throw that poisons the wasm borrow flags.
    (globalThis as unknown as { __physRaw?: () => { rapier: unknown; world: unknown; kcc: unknown } | null }).__physRaw =
      () => (this.rapier ? { rapier: this.rapier, world: this.phys?.world ?? null, kcc: this.kcc } : null);
    // Probe diagnostics: overlap/ceiling census at an arbitrary XZ (no avatar move).
    (globalThis as unknown as { __physProbe?: (x: number, z: number) => { overlap: boolean; ceiling: boolean; terrainOverlap: boolean; feetY: number } | null }).__physProbe =
      (x, z) => {
        if (!this.ready || !atlas.conn) return null;
        this.phys?.world.step(); // scene queries only see colliders integrated by a step
        const structs = this.structureHandles();
        const feetY = groundYIn(atlas.conn, atlas.currentWorldId, x, z);
        return {
          overlap: this.capsuleOverlapsStructure(x, feetY, z, structs),
          ceiling: this.hasCeilingAbove(x, feetY, z, structs),
          terrainOverlap: this.capsuleEmbeddedInTerrain(x, feetY, z),
          feetY,
        };
      };
  }

  private structureHandles(): Set<number> {
    const h = new Set<number>();
    for (const b of this.statics.values()) h.add(b.handle);
    return h;
  }

  /** True when a capsule seated at (x, feetY, z) overlaps a placed-structure collider. */
  private capsuleOverlapsStructure(x: number, feetY: number, z: number, structs: Set<number>): boolean {
    if (!this.phys || !this.rapier) return false;
    // +0.05 lift keeps the terrain surface from grazing the capsule tip.
    const pos = { x, y: feetY + CAPSULE_CENTER_OFFSET + 0.05, z };
    const shape = new this.rapier.Capsule(CAPSULE_HALF_HEIGHT, CAPSULE_RADIUS);
    let hit = false;
    this.phys.world.intersectionsWithShape(pos, { x: 0, y: 0, z: 0, w: 1 }, shape, (col) => {
      const parent = col.parent();
      if (parent && structs.has(parent.handle)) { hit = true; return false; }
      return true;
    });
    return hit;
  }

  /**
   * True when a SHRUNKEN capsule at (x, feetY, z) intersects a TERRAIN trimesh —
   * i.e. genuinely embedded in the ground mesh (seam fold, stamped-road step),
   * not merely standing on it. The shrink (radius −0.1, half-height −0.15) plus
   * the +0.05 lift means normal standing/slope-grazing contact never trips it.
   */
  private capsuleEmbeddedInTerrain(x: number, feetY: number, z: number): boolean {
    if (!this.phys || !this.rapier) return false;
    const terrain = new Set<number>();
    for (const b of this.terrainCols.values()) terrain.add(b.handle);
    if (terrain.size === 0) return false;
    const pos = { x, y: feetY + CAPSULE_CENTER_OFFSET + 0.05, z };
    const shape = new this.rapier.Capsule(CAPSULE_HALF_HEIGHT - 0.15, CAPSULE_RADIUS - 0.1);
    let hit = false;
    this.phys.world.intersectionsWithShape(pos, { x: 0, y: 0, z: 0, w: 1 }, shape, (col) => {
      const parent = col.parent();
      if (parent && terrain.has(parent.handle)) { hit = true; return false; }
      return true;
    });
    return hit;
  }

  /** Highest solid surface at (x,z) within a PAD window above the analytic
   *  ground (+8 m: grading pads and floors, never tower roofs). Excludes the
   *  avatar capsule. Null = nothing collidable there yet. */
  private topSurfaceAt(x: number, z: number, analyticFeetY: number): number | null {
    if (!this.phys || !this.rapier) return null;
    const from = analyticFeetY + 8;
    const ray = new this.rapier.Ray({ x, y: from, z }, { x: 0, y: -1, z: 0 });
    const hit = this.phys.world.castRay(ray, 200, true, undefined, undefined, undefined, undefined,
      (col: RAPIER_NS.Collider) => col !== this.cap);
    if (!hit) return null;
    const toi = (hit as { toi?: number; timeOfImpact?: number });
    const t = toi.timeOfImpact ?? toi.toi ?? 0;
    return from - t;
  }

  /** A world-space ray against every collider but the avatar's capsule
   *  (2026-09-08, impact decals): the first surface within `maxM` with its
   *  normal and class — 'terrain' (a chunk trimesh), 'structure' (a building
   *  proxy / pad box), else 'other'. Null: nothing hit, or physics not ready. */
  /**
   * Is the straight line between two ABSOLUTE points blocked by anything static? The audio runtime's obstruction
   * probe (2026-09-20): a boolean is all it needs, so this skips `rayHit`'s normal and its collider-class walk.
   * The 0.4 m trimmed off each end keeps a ray from starting inside the listener's own capsule or ending inside
   * the emitter's collider (a speaker mounted ON a wall is not behind it). null = physics not ready.
   */
  rayBlocked(ax: number, ay: number, az: number, bx: number, by: number, bz: number): boolean | null {
    if (!this.ready || !this.phys || !this.rapier) return null;
    const dx = bx - ax, dy = by - ay, dz = bz - az;
    const len = Math.hypot(dx, dy, dz);
    if (len < 1.2) return false;
    const ux = dx / len, uy = dy / len, uz = dz / len;
    const ray = new this.rapier.Ray({ x: ax + ux * 0.4, y: ay + uy * 0.4, z: az + uz * 0.4 }, { x: ux, y: uy, z: uz });
    const hit = this.phys.world.castRay(ray, len - 0.8, true, undefined, undefined, undefined, undefined,
      (col: RAPIER_NS.Collider) => col !== this.cap);
    return hit !== null;
  }

  rayHit(from: { x: number; y: number; z: number }, dir: { x: number; y: number; z: number }, maxM: number):
    { x: number; y: number; z: number; nx: number; ny: number; nz: number; dist: number; kind: 'terrain' | 'structure' | 'other' } | null {
    if (!this.ready || !this.phys || !this.rapier) return null;
    const len = Math.hypot(dir.x, dir.y, dir.z);
    if (len < 1e-9 || !(maxM > 0)) return null;
    const d = { x: dir.x / len, y: dir.y / len, z: dir.z / len };
    const ray = new this.rapier.Ray(from, d);
    const hit = this.phys.world.castRayAndGetNormal(ray, maxM, true, undefined, undefined, undefined, undefined,
      (col: RAPIER_NS.Collider) => col !== this.cap);
    if (!hit) return null;
    const toi = hit as unknown as { toi?: number; timeOfImpact?: number };
    const t = toi.timeOfImpact ?? toi.toi ?? 0;
    const parent = hit.collider.parent();
    const h = parent ? parent.handle : -1;
    let kind: 'terrain' | 'structure' | 'other' = 'other';
    for (const b of this.terrainCols.values()) if (b.handle === h) { kind = 'terrain'; break; }
    if (kind === 'other' && this.structureHandles().has(h)) kind = 'structure';
    return { x: from.x + d.x * t, y: from.y + d.y * t, z: from.z + d.z * t, nx: hit.normal.x, ny: hit.normal.y, nz: hit.normal.z, dist: t, kind };
  }

  /**
   * Is there REAL collidable ground at (x,z)? The planetfall arrival gate
   * (field report 2026-08-23: pod ejection seated the player BELOW the
   * terrain because the chunk streamed in after the seat) holds the player
   * behind the cloud cover until this answers true.
   */
  groundReadyAt(x: number, z: number): boolean {
    if (!this.ready || !this.phys || !atlas.conn) return false;
    this.phys.world.step(); // colliders stream in without stepping (the movement-freeze fix)
    const feetY = groundYIn(atlas.conn, atlas.currentWorldId, x, z);
    return this.topSurfaceAt(x, z, feetY) !== null;
  }

  /** Structure roof overhead = probably inside a building volume (trimeshes are
   *  SURFACES — a capsule in a hollow interior reports no overlap, so the
   *  embedded test alone cannot see that trap). */
  private hasCeilingAbove(x: number, feetY: number, z: number, structs: Set<number>): boolean {
    if (!this.phys || !this.rapier) return false;
    const ray = new this.rapier.Ray({ x, y: feetY + 2, z }, { x: 0, y: 1, z: 0 });
    const hit = this.phys.world.castRay(ray, 500, false, undefined, undefined, undefined, undefined,
      (col: RAPIER_NS.Collider) => { const p = col.parent(); return !!p && structs.has(p.handle); });
    return hit !== null;
  }

  /**
   * If the capsule is embedded in a structure collider — or (with `escapeInterior`)
   * roofed inside one — relocate it to the nearest clear, open-sky spot.
   * Returns the new XZ when a relocation happened, else null.
   */
  unstickAvatar(escapeInterior = false, anyClass = false, maxRadiusM = Infinity): { x: number; z: number } | null {
    if (!this.ready || !this.body || !atlas.conn) return null;
    const conn = atlas.conn;
    const cur = this.body.translation();
    const feetY0 = cur.y - this.centerOffset;
    const structs = this.structureHandles();
    // anyClass widens the trap test beyond structures (terrain embeds, wedges) —
    // it must not be gated on a structure existing nearby.
    if (structs.size === 0 && !anyClass) return null;
    // Rapier scene queries (castRay/intersectionsWithShape) only see colliders
    // integrated by a world.step(); colliders stream in without stepping (the
    // movement-freeze fix), so refresh once before testing. unstickAvatar runs
    // at most every 2 s — one extra step is one fixed tick's cost.
    this.phys?.world.step();
    // Probe-visible counters (the headless unstick probe asserts on these).
    const g = globalThis as unknown as { __unstick?: { scans: number; trapped: number; relocations: number; depen: number; wedged: number; last: { x: number; z: number } | null } };
    const dbg = (g.__unstick ??= { scans: 0, trapped: 0, relocations: 0, depen: 0, wedged: 0, last: null });
    dbg.scans++;
    const trapped = this.capsuleOverlapsStructure(cur.x, feetY0, cur.z, structs)
      || (escapeInterior && this.hasCeilingAbove(cur.x, feetY0, cur.z, structs))
      // Any-class escalation (the watchdog's 4 s wedge/terrain path): a capsule
      // pinched between colliders or embedded in the terrain mesh is trapped
      // even though no structure overlap reports it.
      || (anyClass && (this.lastWedged || this.capsuleEmbeddedInTerrain(cur.x, feetY0, cur.z)));
    if (!trapped) return null;
    dbg.trapped++;
    for (const r of PhysicsModule.UNSTICK_RINGS) {
      if (r > maxRadiusM) break; // beyond what the server's unstick envelope would accept from its (lagging) row
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 + r * 0.7; // stagger angles ring-to-ring
        const x = cur.x + Math.cos(a) * r;
        const z = cur.z + Math.sin(a) * r;
        const feetY = groundYIn(conn, atlas.currentWorldId, x, z);
        if (!this.capsuleOverlapsStructure(x, feetY, z, structs)
          && !this.capsuleEmbeddedInTerrain(x, feetY, z)
          && !this.hasCeilingAbove(x, feetY, z, structs)) {
          this.teleportAvatar(x, z);
          dbg.relocations++;
          dbg.last = { x, z };
          return { x, z };
        }
      }
    }
    return null;
  }

  /**
   * Move the avatar by an intent direction (collide-and-slide + gravity + grounding).
   * Returns the resolved feet position + the actual horizontal displacement.
   */
  /**
   * Rebuild a POISONED Rapier world: once a wasm borrow flag sticks (an
   * exception unwound mid-call), every later call throws "recursive use of
   * an object" forever — a dead session. Cure: abandon the old world, build
   * a fresh one, re-add every collider from the retained sources, reseat the
   * capsule. Throttled so a persistent poisoner can't thrash rebuilds.
   */
  private rebuildPoisonedWorld(reason: string): boolean {
    const now = performance.now();
    if (now - this.lastRebuildMs < 5000) return false;
    if (!this.rapier || !this.PhysCls || !this.KccCls) return false;
    this.lastRebuildMs = now;
    Logger.error('Physics', `Rapier world poisoned (${reason}) — rebuilding with ${this.staticSrc.size} statics + ${this.terrainSrc.size} terrain colliders`);
    try { this.phys?.dispose(); } catch { /* the poisoned world may refuse even disposal — abandon it to GC */ }
    const RAPIER = this.rapier;
    this.phys = new this.PhysCls({ x: 0, y: -9.81, z: 0 });
    this.body = this.phys.createBody(RAPIER.RigidBodyDesc.kinematicPositionBased()
      .setTranslation(atlas.localX, atlas.localY + this.centerOffset, atlas.localZ));
    this.cap = this.phys.createCollider(RAPIER.ColliderDesc.capsule(this.crouched_ ? CROUCH_HALF_HEIGHT : CAPSULE_HALF_HEIGHT, CAPSULE_RADIUS), this.body);
    this.cap.setActiveCollisionTypes(RAPIER.ActiveCollisionTypes.DEFAULT | RAPIER.ActiveCollisionTypes.KINEMATIC_FIXED);
    this.kcc = new this.KccCls(this.phys, {
      snapToGround: 0.5,
      autoStepMaxHeight: 0.8,
      autoStepMinWidth: 0.15,
      maxSlopeClimbAngle: (62 * Math.PI) / 180,
      minSlopeSlideAngle: (68 * Math.PI) / 180,
    });
    this.statics.clear();
    this.terrainCols.clear();
    for (const [id, src] of this.staticSrc) {
      const desc = colliderDescFromProxy(RAPIER, scaleProxy(src.proxy, src.t.scale));
      if (!desc) continue;
      const body = this.phys.createBody(RAPIER.RigidBodyDesc.fixed()
        .setTranslation(src.t.x, src.t.y, src.t.z)
        .setRotation({ x: 0, y: Math.sin(src.t.yaw / 2), z: 0, w: Math.cos(src.t.yaw / 2) }));
      this.phys.createCollider(desc, body);
      this.statics.set(id, body);
    }
    for (const [key, src] of this.terrainSrc) {
      const body = this.phys.createBody(RAPIER.RigidBodyDesc.fixed().setTranslation(src.x, 0, src.z));
      this.phys.createCollider(RAPIER.ColliderDesc.trimesh(src.v, src.i), body);
      this.terrainCols.set(key, body);
    }
    this.vy = 0;
    this._grounded = true;
    this._spawnGrounded = true;
    this.snapOff = false;
    this.phys.world.step();
    return true;
  }

  /** True when a Rapier failure is the stuck-borrow poisoning (vs a real bug). */
  private static isPoisonError(e: unknown): boolean {
    return /recursive use|unsafe aliasing/.test(String((e as Error)?.message ?? e));
  }

  moveAvatar(intentX: number, intentZ: number, speed: number, dt: number): AvatarMove | null {
    try {
      return this.moveAvatarInner(intentX, intentZ, speed, dt);
    } catch (e) {
      if (PhysicsModule.isPoisonError(e)) { this.rebuildPoisonedWorld('moveAvatar'); return null; }
      throw e;
    }
  }

  private moveAvatarInner(intentX: number, intentZ: number, speed: number, dt: number): AvatarMove | null {
    if (!this.ready || !this.kcc || !this.cap || !this.body || !this.phys) return null;
    const t = this.body.translation();
    // Depenetrate-then-sweep: push out of any penetrating contact BEFORE the
    // KCC sweep. A capsule already inside a collider (streamed-in structure,
    // reconcile snap, trimesh seam fold) is a state the sweep can never fix —
    // it only avoids NEW penetration. Manifolds exist because moveAvatar steps
    // the world every tick. Clamped per tick, so deep embeds walk out over a
    // few ticks instead of popping.
    // A committed mantle owns the body for MANTLE_TICKS (build-now wave 6).
    if (this.mantle) return this.stepMantle(t, dt);
    const push = this.kcc.depenetrate(this.phys.world, this.cap);
    if (push.overlaps > 0) {
      // Eager ??= — the lazy `if (g.__unstick)` dropped every increment that
      // happened before the first watchdog scan created the object, so a fast
      // depenetration (freed in <2 s) read depen=0 in the probe.
      const g = globalThis as unknown as { __unstick?: { scans: number; trapped: number; relocations: number; depen: number; wedged: number; last: { x: number; z: number } | null } };
      const dbg = (g.__unstick ??= { scans: 0, trapped: 0, relocations: 0, depen: 0, wedged: 0, last: null });
      dbg.depen++;
    }
    // ── Interactive water (2026-09-11): wading slows with depth, deep water
    // FLOATS the avatar (a critically damped spring holds the chest at the
    // live surface — swell and wakes included), a stroke lifts, an entry at
    // speed splashes. The still level and the ground under the feet decide
    // the mode (lib/water_physics); off (the graphics flag) = dry everywhere.
    const feetY0 = t.y - this.centerOffset;
    const connW = atlas.conn;
    const fluid = activePreset().fluidPhysics !== false && atlas.interiorId === null && atlas.caveId === null && connW !== null;
    const still = fluid ? waterStillAt(t.x, t.z) : null;
    const groundW = fluid && still !== null && connW ? groundYIn(connW, atlas.currentWorldId, t.x, t.z) : feetY0;
    const mode: WaterMode = fluid ? waterModeFor(still, groundW) : 'dry';
    if (mode !== 'dry' && this.waterMode_ === 'dry' && this.vy < -SPLASH_MIN_IMPACT_MPS && still !== null) {
      const sp = splashFor(-this.vy, 1.2);
      splashWater(t.x, still, t.z, sp.count, sp.speed);
      stampWater(t.x, t.z, sp.stamp.strength, sp.stamp.radius);
      sfx('sfx_water_splash', { gain: Math.min(0.7, 0.3 + sp.count / 200), cooldownMs: 300 });
    }
    this.waterMode_ = mode;
    let speedMul = 1;
    let swimDy: number | null = null;
    if (mode === 'swim' && still !== null) {
      const live = waterLiveAt(t.x, t.z) ?? still;
      const stroke = this.strokeRequested;
      const r = swimVerticalStep(this.vy, feetY0, live, dt, stroke);
      this.vy = r.vy;
      swimDy = r.feetY - feetY0;
      this._grounded = false;
      this.airT = 0;
      // No snap-to-ground while afloat: the feet ride 1.15 m under the surface
      // and the swim threshold is 1.25 m, so at the shallow end the bed sits
      // within Rapier's 0.5 m snap — every down-stroke of the float spring got
      // glued to the bed (a bob at the wade→swim line). The wade/dry branch
      // below restores the snap once the stroke is falling (2026-09-12).
      if (!this.snapOff) { this.kcc.disableSnapToGround(); this.snapOff = true; }
    } else if (mode === 'wade' && still !== null) {
      speedMul = wadeSpeedFactor(still - groundW);
    }
    this.strokeRequested = false;
    if (swimDy === null) {
      // Press to ground when grounded and not ascending; otherwise integrate gravity.
      this.vy = this._grounded && this.vy <= 0 ? -2 : this.vy - 9.81 * this.gravityScale * dt;
      // Restore snap-to-ground once the jump apex passes (falling again).
      if (this.snapOff && this.vy <= 0) { this.kcc.enableSnapToGround(0.5); this.snapOff = false; }
      // Terminal velocity: a long slide/fall must not wind vy up without bound
      // (the KCC turns a huge downward desire into a huge along-slope slide).
      if (this.vy < -25) this.vy = -25;
    }
    const desired = { x: intentX * speed * speedMul * dt, y: swimDy ?? this.vy * dt, z: intentZ * speed * speedMul * dt };
    const c = this.kcc.move(this.cap, desired, dt);
    this.lastWedged = this.kcc.lastMoveWedged;
    // THE ICE-SKATES LAW (field bug 2026-08-22: "go flying on the terrain, then
    // rubber-band back"): the server integrates the sent intent at ≤ speed, so
    // a client step it cannot follow — a gravity slide down a >68° facet, or
    // the depenetration push (0.25 m/tick = 15 m/s) — opens a gap the next
    // acknowledged prediction turns into a snap back. Cap the TOTAL horizontal
    // displacement (push + move) at the server's own step, and report that
    // total as the movement so the row follows the real path (pushes included).
    let hx = push.x + c.x, hz = push.z + c.z;
    const hl = Math.hypot(hx, hz);
    const maxH = speed * dt;
    if (hl > maxH) { const k = maxH / hl; hx *= k; hz *= k; this.diag.capped++; }
    if (hl / dt > this.diag.maxRawMps) this.diag.maxRawMps = hl / dt;
    let nx = t.x + hx, ny = t.y + push.y + c.y, nz = t.z + hz;
    this._grounded = this.kcc.grounded && this.vy <= 0; // rising = airborne even if the KCC still touches
    if (this._grounded) {
      // Falling→grounded edge: a real fall (past the coyote window + a beat)
      // lands with a thud, louder for longer drops. Micro-bumps stay silent.
      if (this.airT > 0.35 && !atlas.submerged && this.waterMode_ === 'dry') {
        sfx('sfx_land', { gain: Math.min(0.6, 0.22 + this.airT * 0.18), cooldownMs: 250 });
      }
      this.airT = 0;
    } else this.airT += dt;

    // Terrain clamp: when NOT standing on a structure collider, rest on the procedural ground.
    const conn = atlas.conn;
    if (conn) {
      const terrainCenterY = groundYIn(conn, atlas.currentWorldId, nx, nz) + this.centerOffset;
      // Inside a cave? Decided HERE at the body's position (see setCaveTest).
      const caveHere = this.caveTest ? this.caveTest(nx, ny, nz) : null;
      if (atlas.caveId !== caveHere) atlas.caveId = caveHere;
      if (!this._spawnGrounded) {
        // One-time spawn grounding: the body is created at SPAWN_Y, which sits ABOVE the
        // un-region'd terrain (height ~-2.47 at origin), so without this it free-falls — and
        // the fall starves the horizontal move-send, freezing the avatar. Seat it on the ground.
        ny = terrainCenterY + PhysicsModule.DROP_IN_M; this._grounded = false; this._spawnGrounded = true; this.vy = 0; // drop-in (see DROP_IN_M)
      } else if (this.waterMode_ !== 'swim' && atlas.interiorId === null && atlas.caveId === null && this.vy <= 0 && push.overlaps > 0 && push.y < -0.001 && ny < terrainCenterY + 0.05) {
        // OVERLAP-FIGHT HOLD (the login-bounce field bug, 2026-08-23): a
        // static collider overlapping the spawn cell (a slab streamed in
        // over the pad) put the terrain clamp and the depenetration pass in
        // a 60 Hz tug-of-war — clamp yanks the capsule up to the rendered
        // ground, depen shoves it back into/out of the overlap, every tick,
        // 0.5 m of violent bounce (reproduced: verify/spawn-bounce F2,
        // legacy = 199 direction flips). While the overlap persists at/
        // below the rendered ground, the clamp WINS UNCONDITIONALLY — the
        // capsule stands still at the rendered ground (no depen jitter, no
        // grounded-flag alternation; it is kinematic, an overlap does not
        // hurt) — and `clampFight` counts up so the avatar watchdog
        // relocates to clear ground at ~1 s. Jumps (vy>0) exempt.
        //
        // push.y < 0 REQUIRED (field report #2, same day: "stuck in terrain,
        // can't move"): when the capsule is embedded in the TERRAIN trimesh
        // itself (login onto a low-LOD chunk whose collider runs above the
        // analytic ground), depenetration pushes UP — the same direction as
        // the clamp. That is not a fight: let physics raise the capsule onto
        // the real trimesh (the pre-fix behavior, field-stable for months).
        // Holding there instead PINNED the player inside the terrain, and
        // the watchdog relocation landed in the same field-wide mismatch.
        this.clampFightTicks++;
        ny = terrainCenterY; this._grounded = true;
      } else if (this.waterMode_ !== 'swim' && atlas.interiorId === null && atlas.caveId === null && !this._grounded && this.vy <= 0 && ny < terrainCenterY) {
        // Falling below the rendered ground → land. (vy>0 exempt: never yank a
        // fresh jump back down to the terrain clamp.) INTERIORS exempt too:
        // on a sloped site the raw terrain runs METERS above the interior
        // slabs, and a single grounded-flicker mid-stairs snapped the player
        // onto the dirt inside the shell — the "still dropping floors" field
        // bug the flat-pad harness could never reproduce. Inside a building
        // the KCC + slab colliders own grounding; the exit path restores this
        // clamp the moment the player leaves.
        ny = terrainCenterY; this._grounded = true;
        this.clampFightTicks = 0;
      } else if (push.overlaps === 0) {
        this.clampFightTicks = 0;
      }
    }
    if (this._grounded && this.vy < 0) this.vy = 0;

    this.body.setNextKinematicTranslation({ x: nx, y: ny, z: nz });
    this.phys.world.step();
    return { x: nx, z: nz, feetY: ny - this.centerOffset, movedX: hx, movedZ: hz, grounded: this._grounded };
  }

  dispose(): void {
    this._disposed = true; // prevent a late-arriving Rapier init .then from resurrecting state
    this.phys?.dispose();
    this.phys = null; this.kcc = null; this.body = null; this.cap = null; this.rapier = null; this.statics.clear(); this.terrainCols.clear();
    this.ready = false;
  }
}

/** Pre-scale a proxy's geometry (Rapier colliders bake absolute coordinates). */
function scaleProxy(proxy: ColliderProxy, scale: number): ColliderProxy {
  if (Math.abs(scale - 1) < 1e-4) return proxy;
  const s = (a?: number[]) => (a ? a.map((v) => v * scale) : a);
  return { ...proxy, positions: s(proxy.positions), points: s(proxy.points) };
}
// R10: PRE-SCALED proxies — one scaled copy per (proxy, scale) instead of a
// fresh map() of every vertex per placement (props repeat a scale hundreds
// of times per city; the hull points are shared per prefab key).
const scaledCache = new WeakMap<ColliderProxy, Map<number, ColliderProxy>>();
export function scaledProxy(proxy: ColliderProxy, scale: number): ColliderProxy {
  if (Math.abs(scale - 1) < 1e-4) return proxy;
  const k = Math.round(scale * 1000);
  let m = scaledCache.get(proxy);
  if (!m) { m = new Map(); scaledCache.set(proxy, m); }
  let out = m.get(k);
  if (!out) { out = scaleProxy(proxy, scale); m.set(k, out); }
  return out;
}
